/**
 * @file Shared key-value table row helpers.
 *
 * `.kv` tables are the project's canonical "labelled field" layout
 * (Identity / Assignment / Reconcile / Sync cards). Centralising the row
 * factories here so /me, /people/:id, /devices/:id and /settings render
 * exactly the same shape.
 */

import { el } from '../utils.js';

/**
 * Single key/value row.
 *
 * @param {string} k          Label (uppercase styling is applied via CSS).
 * @param {string|Node} v     Value. Strings auto-wrapped in a span.
 * @param {boolean} readonly  Adds `.readonly` to the value cell for muted styling.
 * @param {boolean} mono      Wraps string values in a monospace span (SN, IDs).
 */
export function kvRow(k, v, readonly = false, mono = false) {
  const vNode = (typeof v === 'string' || typeof v === 'number')
    ? el('span', mono ? { class: 'sn' } : null, String(v))
    : v;
  return el('tr', null,
    el('td', { class: 'k' }, k),
    el('td', { class: `v${readonly ? ' readonly' : ''}` }, vNode),
  );
}

/**
 * Bulk variant — `pairs` is an array of `[label, value]` tuples. Empty
 * strings or null/undefined values render as an em-dash so the row stays
 * vertically aligned with siblings.
 */
export function kvRows(pairs) {
  return pairs.map(([k, v]) => el('tr', null,
    el('td', { class: 'k' }, k),
    el('td', { class: 'v' }, v == null || v === '' ? '—' : v),
  ));
}
